import { DocumentationAreaActorOrderRetrievalMethod } from "./DocumentationAreaActorOrderRetrievalMethod";
import { OrderId } from "./OrderId";
import { OrderIdentityVerification } from "./OrderIdentityVerification";
import { OrderModel } from "./OrderModel";
import { OrderOtherDocument } from "./OrderOtherDocument";
import { OrderPayslip } from "./OrderPayslip";
import { OrderResponse } from "./OrderResponse";
import { OrderStatus } from "./OrderStatus";
import { OrderTypes } from "./OrderTypes";
import { ExtractionDetails } from "./ExtractionDetails";
import { ExtractionState, resolveExtractionState } from "./ExtractionState";

export abstract class BaseOrder<TOrderRequest> {
  protected readonly id: OrderId;
  protected readonly type: OrderTypes;
  protected readonly request: TOrderRequest;
  protected readonly responses: OrderResponse[];
  protected readonly retrievalMethods: DocumentationAreaActorOrderRetrievalMethod[];
  protected status: OrderStatus;

  protected constructor(
    id: OrderId,
    type: OrderTypes,
    request: TOrderRequest,
    responses: OrderResponse[],
    retrievalMethods: DocumentationAreaActorOrderRetrievalMethod[],
    status: OrderStatus,
  ) {
    this.id = id;
    this.type = type;
    this.request = request;
    this.responses = responses;
    this.retrievalMethods = retrievalMethods;
    this.status = status;
  }

  public getId(): OrderId {
    return this.id;
  }

  public getType(): OrderTypes {
    return this.type;
  }

  public getRequest(): TOrderRequest {
    return this.request;
  }

  public getResponses(): OrderResponse[] {
    return this.responses;
  }

  public getRetrievalMethods(): DocumentationAreaActorOrderRetrievalMethod[] {
    return this.retrievalMethods;
  }

  public validResponses() {
    return this.responses.filter((response) => response.isValid());
  }

  public invalidResponses(): OrderResponse[] {
    return this.responses.filter((response) => response.isInvalid());
  }

  public getStatus(): OrderStatus {
    return this.status;
  }

  public getExtractionDetails(): ExtractionDetails | null {
    return null;
  }

  public getExtractionState(): ExtractionState {
    return resolveExtractionState({
      orderStatus: this.status,
      hasValidResponses: this.hasValidResponses(),
      hasPendingReviewResponses: this.hasPendingReviewResponses(),
      hasInvalidResponses: this.invalidResponses().length > 0,
      extractionDetails: this.getExtractionDetails(),
    });
  }

  public isIdle(): boolean {
    return this.status === OrderStatus.IDLE;
  }

  public isProcessing(): boolean {
    return this.status === OrderStatus.PROCESSING;
  }

  public isCancelled(): boolean {
    return this.status === OrderStatus.CANCELLED;
  }

  public isPayslip(): this is OrderPayslip {
    return this.type === "payslip";
  }

  public isModel(): this is OrderModel {
    return this.type === "model";
  }

  public isOtherDocument(): this is OrderOtherDocument {
    return this.type === "other_document";
  }

  public isIdentityVerification(): this is OrderIdentityVerification {
    return this.type === "identity_verification";
  }

  public hasResponses(): boolean {
    return this.responses.length > 0;
  }

  public hasValidResponses(): boolean {
    return this.responses.some((response) => response.isValid());
  }

  public hasPendingReviewResponses(): boolean {
    return this.responses.some((response) => response.isPending());
  }

  private setStatus(newStatus: OrderStatus): void {
    this.status = newStatus;
  }

  public setToIdle(): void {
    this.setStatus(OrderStatus.IDLE);
  }

  public setToProcessing(): void {
    this.setStatus(OrderStatus.PROCESSING);
  }

  /**
   *
   * @returns true if the user can upload more files, which is true in the following scenarios:
   * for type `models`, if there are no valid files and the retrievalMethods include `manual_upload`.
   * For other types, always that the retrievalMethods include `manual_upload`
   */
  public canManuallyUpload(): boolean {
    const canManuallyUpload = this.retrievalMethods.some(
      (method) => method.isManualUpload() && !method.isReadonly(),
    );
    if (this.getType() === OrderTypes.MODEL) {
      return !this.hasValidResponses() && canManuallyUpload;
    }
    return canManuallyUpload;
  }

  public canAutomaticallyExtract(): boolean {
    return this.retrievalMethods.some((method) => method.isAutomaticExtraction());
  }

  public canOnlyAutomaticallyExtract(): boolean {
    return (
      this.retrievalMethods.some((method) => method.isAutomaticExtraction()) &&
      this.retrievalMethods.length === 1
    );
  }
}
